import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './SeriesThreePlusConfirmationModal.module.scss';
import Popup from 'reactjs-popup';
import { ReactComponent as CloseIcon } from '../../assets/images/close-navigation.svg';
import SeriesThreePlusModule from '../../assets/images/series_three_plus_module.png';
import { PreviewPrice } from './SeriesThreePlusSelection';
import { centsToDollars } from '../../lib/util';
import { seriesThreePlusUpgrade } from '../../lib/analytics/events';
import EditShipping, { AddressFields } from './EditShipping';
import EditBilling, { BillingFields } from './EditBilling';
import { Address, BillingInfo, gqlTypes } from '../../types';
import { useMutation } from '@apollo/client';
import { updateBillingInfoMutation } from '../../graphql-operations/BillingAccount.graphql';
import { updateShippingAddressMutation } from '../../graphql-operations/ShippingInfo.graphql';
import Loading from '../../components/Loading';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';

interface ISeriesThreePlusConfirmationModalProps {
  trigger: JSX.Element;
  previewPrice: PreviewPrice;
  renewalPriceInCents: number;
  renewalDate?: string;
  billingInfo?: BillingInfo;
  shippingAddress?: Address;
  onAccept?: () => void;
  onUpdateBilling?: () => void;
}

export default function SeriesThreePlusConfirmationModal({
  trigger,
  billingInfo,
  shippingAddress,
  previewPrice,
  renewalPriceInCents,
  renewalDate,
  onAccept = () => {},
  onUpdateBilling,
}: ISeriesThreePlusConfirmationModalProps) {
  const { t } = useTranslation();
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(false);
  const [showEditShipping, setShowEditShipping] = useState(false);
  const [showEditBilling, setShowEditBilling] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const growthBookEnabled = useIsGrowthBookEnabled();
  const isBfcmUpgradeEnabled =
    growthBookEnabled && getFiGrowthBook().getFeatureValue<boolean>('ecom-bfcm-upgrade', false);

  const [updateBillingMutation, { loading: mutationLoading }] = useMutation<
    gqlTypes.updateBillingInfo,
    gqlTypes.updateBillingInfoVariables
  >(updateBillingInfoMutation);

  const [updateShippingMutation, { loading: shippingMutationLoading }] = useMutation<
    gqlTypes.updateShippingAddress,
    gqlTypes.updateShippingAddressVariables
  >(updateShippingAddressMutation);

  const handleUpdateAddress = (addressFields: AddressFields, recaptchaToken: string) => {
    if (shippingMutationLoading) {
      return;
    }

    setIsUpdating(true);
    updateShippingMutation({
      variables: {
        firstName: billingInfo?.firstName || '',
        lastName: billingInfo?.lastName || '',
        address: {
          line1: addressFields.line1,
          line2: addressFields.line2 || null,
          city: addressFields.city,
          state: addressFields.state,
          zip: addressFields.zip,
          country: addressFields.country,
        },
        recaptchaToken,
      },
    })
      .then((result) => {
        if (result.data?.updateBillingAccount?.address) {
          setShowEditShipping(false);
          if (onUpdateBilling) {
            onUpdateBilling();
          }
        } else {
          throw new Error(t('no_updated_shipping_address'));
        }
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const handleUpdateBilling = (billingFields: BillingFields, recaptchaToken: string) => {
    if (mutationLoading) {
      return;
    }

    setIsUpdating(true);
    setShowEditBilling(false);
    updateBillingMutation({
      variables: {
        input: {
          billingInfo: {
            token: billingFields.paymentInfo.token || '',
          },
        },
        recaptchaToken,
      },
    })
      .then((result) => {
        if (result.data?.updateBillingAccount?.billingInfo) {
          if (onUpdateBilling) {
            onUpdateBilling();
          }
        } else {
          throw new Error(t('no_updated_billing_information'));
        }
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  return (
    <Popup className="s3-popup" trigger={trigger} modal>
      {(close: () => void) => (
        <>
          {showEditShipping && shippingAddress && (
            <EditShipping
              onUpdateAddress={handleUpdateAddress}
              onCancel={() => setShowEditShipping(false)}
              initialAddress={shippingAddress as AddressFields}
            />
          )}
          {showEditBilling && !showEditShipping && billingInfo && (
            <EditBilling
              onUpdateBilling={handleUpdateBilling}
              onCancel={() => setShowEditBilling(false)}
              initialBilling={billingInfo}
              shippingAddress={billingInfo.address}
            />
          )}
          {!showEditShipping && !showEditBilling && isUpdating && <Loading />}
          {!isUpdating && !showEditShipping && !showEditBilling && (
            <div className={styles.container}>
              <div className={styles.header}>
                <div className={styles.title}>{t('checkout')}</div>
                <div className={styles.closeIcon}>
                  <CloseIcon
                    onClick={() => {
                      seriesThreePlusUpgrade.confirmationModalGoBack();
                      close();
                    }}
                  />
                </div>
              </div>
              <div className={styles.body}>
                <div className={styles.offerDetailsContainer}>
                  <div className={styles.upsellSubscriptionRow} style={{ display: 'flex', flexDirection: 'row' }}>
                    <img className={styles.module} src={SeriesThreePlusModule} alt={t('series_three_plus_module')} />
                    <div
                      className={styles.upsellSubscriptionColumn}
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <p className={styles.upsellSubscriptionTermText}>{t('fi_series_3_plus')}</p>
                      <p className={styles.upsellSubscriptionDescription}>{t('device_only')}</p>
                    </div>
                    <p className={styles.upsellSubscriptionPriceText}>{isBfcmUpgradeEnabled ? '$79' : '$99'}</p>
                  </div>
                </div>
                {shippingAddress && (
                  <div className={styles.addressContainer}>
                    <div className={styles.addressHeader}>
                      <p className={styles.addressTitle}>{t('shipping')}</p>
                      <button className={styles.editButton} onClick={() => setShowEditShipping(true)}>
                        {t('edit')}
                      </button>
                    </div>
                    <p className={styles.addressLine1}>{shippingAddress.line1}</p>
                    <p className={styles.addressLine2}>{shippingAddress.line2}</p>
                    <p
                      className={styles.addressCityStateZip}
                    >{`${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`}</p>
                  </div>
                )}
                {billingInfo && billingInfo.paymentInfo && (
                  <div className={styles.billingInfoContainer}>
                    <div className={styles.billingInfoHeader}>
                      <p className={styles.billingInfoTitle}>{t('billing')}</p>
                      <button className={styles.editButton} onClick={() => setShowEditBilling(true)}>
                        {t('edit')}
                      </button>
                    </div>
                    {billingInfo.paymentInfo.__typename === 'ObfuscatedCardInfo' && (
                      <>
                        <p className={styles.billingInfoValue}>
                          <Trans
                            i18nKey="card_ending_in_last_four_no_mask"
                            values={{
                              lastFour: billingInfo.paymentInfo.lastFour,
                            }}
                          />
                        </p>
                        {billingInfo.paymentInfo.month && billingInfo.paymentInfo.year && (
                          <p className={styles.billingInfoExpiry}>
                            {t('expires_month_year_no_mask', {
                              month: billingInfo.paymentInfo.month,
                              year: billingInfo.paymentInfo.year,
                            })}
                          </p>
                        )}
                      </>
                    )}
                    {billingInfo.paymentInfo.__typename === 'PayPalInfo' && (
                      <p className={styles.billingInfoValue}>{t('paypal')}</p>
                    )}
                  </div>
                )}
                {isBfcmUpgradeEnabled && (
                  <div className={styles.taxContainer}>
                    <div className={styles.costPartTitle}>{t('subtotal')}</div>
                    <div className={styles.costPartValue}>{'$79.00'}</div>
                  </div>
                )}
                {!isBfcmUpgradeEnabled && (
                  <div className={styles.taxContainer}>
                    <div className={styles.costPartTitle}>{t('subtotal')}</div>
                    <div className={styles.costPartValue}>{'$99.00'}</div>
                  </div>
                )}
                <div className={styles.taxContainer}>
                  <div className={styles.costPartTitle}>{t('shipping')}</div>
                  <div className={styles.costPartValue}>
                    {previewPrice.shippingInCents > 0
                      ? `${'$'}${centsToDollars(previewPrice.shippingInCents)}`
                      : t('free')}
                  </div>
                </div>
                {previewPrice.taxInCents > 0 && (
                  <div className={styles.taxContainer}>
                    <div className={styles.costPartTitle}>{t('taxes')}</div>
                    <div className={styles.costPartValue}>
                      {'$'}
                      {centsToDollars(previewPrice.taxInCents)}
                    </div>
                  </div>
                )}
                <div className={styles.totalDueContainer}>
                  <div className={styles.totalDueTitle}>{t('total')}</div>
                  <div className={styles.totalDueValue}>
                    {'$'}
                    {centsToDollars(previewPrice.totalInCents)}
                  </div>
                </div>
                <button
                  className={styles.confirmOrderButton}
                  onClick={() => {
                    setConfirmButtonDisabled(true);
                    onAccept();
                  }}
                  disabled={confirmButtonDisabled}
                >
                  {t('confirm_order')}
                </button>
                <div className={styles.disclaimer}>
                  <Trans
                    i18nKey="current_membership_remains_renews_at_price_on_date_by_confirming_order_you_accept_terms"
                    values={{
                      price: centsToDollars(renewalPriceInCents, 0),
                      date: renewalDate,
                    }}
                    components={{
                      tosLink: (
                        <a href="https://tryfi.com/tos" target="_blank" rel="noopener noreferrer">
                          {/* Content will be provided by Trans component */}
                        </a>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Popup>
  );
}
